/**
 * How a list's filter fields apply what is typed into them (#111).
 *
 * A filter applies when its field is left, which is how the portal worked before phones. Two things
 * a phone needed on top of that:
 *
 * - **Enter.** A phone keyboard's Go key is how its user says "done", and it leaves nothing, so Enter
 *   applies the filter too. On a touch screen it does so by leaving the field, which also puts the
 *   keyboard away; anywhere else it applies directly and the focus stays where the user is. Either
 *   way the filter applies once.
 * - **Dates.** A phone's date picker leaves the field focused after the choice, so a date applies as
 *   soon as it changes. But a keyboard edits a date a segment at a time and reports every step as a
 *   whole value (Backspace on the day gives '', the next digit the 1st), so while a key is being
 *   typed into the field, a change waits for the field to be left, or for Enter.
 */

import { useRef, type ChangeEvent, type FocusEvent, type KeyboardEvent } from 'react'

const touchScreen = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches

/** Enter applies `apply` once: by leaving the field on a touch screen, directly anywhere else. */
export function applyOnEnter(apply: (value: string) => void) {
  return (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
    if (touchScreen()) event.currentTarget.blur()
    else apply(event.currentTarget.value)
  }
}

/**
 * Whether a date field's value is a day worth asking the server about: empty (cleared), or a
 * four-digit year. Chrome reports 0002, 0020, 0202 while a year is typed a digit at a time; this is
 * the guard for a change no key announced.
 */
export const isAskableDate = (value: string) => value === '' || /^[1-9]\d{3}-/.test(value)

/** The handlers for a date filter field: a pick applies at once, a typed date when it is finished. */
export function useDateFilter(apply: (day: string) => void) {
  const typing = useRef(false)
  return {
    onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
      if (event.key === 'Enter') {
        typing.current = false
        applyOnEnter(apply)(event)
      } else if (event.key !== 'Tab' && event.key !== 'Shift') {
        typing.current = true
      }
    },
    onChange(event: ChangeEvent<HTMLInputElement>) {
      if (!typing.current && isAskableDate(event.currentTarget.value)) apply(event.currentTarget.value)
    },
    onBlur(event: FocusEvent<HTMLInputElement>) {
      typing.current = false
      apply(event.currentTarget.value)
    },
  }
}
